import React, { CSSProperties, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight, Info, MessagesSquare } from 'lucide-react';

import { ISRV2RapportCollapse, ISimulationReportV2, ISRV2RubricParameter, ISRV2RapportCollapseConversation } from '../../../../types/services.interface';
import { ESimulationStageKey } from '../../../../types/entities.enum';

type TRubricLevel = 'highly-skilled' | 'proficient' | 'gap' | 'priority-gap';
interface AnalysisV2ViewProps {
    report: ISimulationReportV2;
};
export const AnalysisV2View = ({ report }: AnalysisV2ViewProps) => {
    const getOverallScore = (): number => {
        return Number((report.rubric_parameters.reduce((acc, curr) => acc + curr.score, 0) / report.rubric_parameters.length).toFixed(1));
    };

    const getProficiencyCategory = () => {
        const score = getOverallScore();

        if (score <= 1.9) return "Practicing Leader";
        if (score <= 2.9) return "Emerging Leader";
        if (score <= 3.9) return "Proficient Leader";
        return "Highly Skilled Leader";
    };

    const getRubricLevel = (score: number): { title: string; level: TRubricLevel; color: string; } => {
        if (score >= 3.5) return { title: 'Highly Skilled', level: 'highly-skilled', color: '#11a070' };
        if (score >= 3) return { title: 'Proficient', level: 'proficient', color: '#4338CA' };
        if (score >= 2) return { title: 'Gap', level: 'gap', color: '#da8e0b' };

        return { title: 'Priority Gap', level: 'priority-gap', color: '#c73434' };
    };

    const getExecutiveNarrative = () => {
        const intro = `<p>Congratulations on completing this simulation! Here's a detailed breakdown of your interaction, your areas of strength and opportunities to improve.</p>`;

        // Extract existing paragraphs from report.executive_narrative
        const paragraphs = report.executive_narrative
            .match(/<p>.*?<\/p>/g) || [];

        // Add prefixes
        if (paragraphs[0]) paragraphs[0] = paragraphs[0].replace("<p>", "<p><strong>Proficiency:</strong> ");
        if (paragraphs[1]) paragraphs[1] = paragraphs[1].replace("<p>", "<p><strong>Examples:</strong> ");
        if (paragraphs[2]) paragraphs[2] = paragraphs[2].replace("<p>", "<p><strong>Use:</strong> ");

        return intro + paragraphs.slice(0, 3).join("");
    };

    return (
        <div className='complete_data__analysis__v2'>
            <div className="complete_data__analysis__v2__head">
                <div className="complete_data__analysis__v2__head__left">
                    <p className='complete_data__analysis__v2__head__report_title'>Leadership Simulation Results & Development Summary</p>
                    <p className='complete_data__analysis__v2__head__report_description'>A behavioral audit of leadership influence and cognitive resilience during the <strong>"{report.simulation_name}"</strong> simulation.</p>
                </div>
                <div className="complete_data__analysis__v2__head__right">
                    <p><strong>Role:</strong> {report.current_role} ({report.current_level})</p>
                    <p><strong>Next Milestone:</strong> {report.milestone_role} ({report.milestone_level})</p>
                    <p>MINDSHIELD PROPRIETARY DASHBOARD</p>
                </div>
            </div>
            <div className="complete_data__analysis__v2__executive_narrative">
                <div className='complete_data__analysis__v2__executive_narrative__left_border' />
                <p className='complete_data__analysis__v2__section_title'>Executive Summary</p>
                <div className='complete_data__analysis__v2__executive_narrative__content'>
                    <div className='complete_data__analysis__v2__executive_narrative__content__left'>
                        <div className="complete_data__analysis__v2__executive_narrative__content__current_profile">
                            Current Profile: {report.cognitive_profile}
                        </div>
                        <ExecutiveNarrativeContentDetails
                            score={getOverallScore()}
                            title={getProficiencyCategory()}
                            custClass='d-none-desktop'
                        />
                        <div
                            className='complete_data__analysis__v2__executive_narrative__content__text'
                            dangerouslySetInnerHTML={{ __html: getExecutiveNarrative() }}
                        />
                    </div>
                    <div className='complete_data__analysis__v2__executive_narrative__content__right'>
                        <ExecutiveNarrativeContentDetails
                            score={getOverallScore()}
                            title={getProficiencyCategory()}
                            custClass='d-none-mobile'
                        />
                        <div className='complete_data__analysis__v2__executive_narrative__content__use'>
                            <p>How to Use This Summary Report</p>
                            <p>Focus on the specific recommendations highlighted in your report. Practice the suggested alternatives in low-stakes situations before applying them in critical moments.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="complete_data__analysis__v2__mastery_framework">
                <p className='complete_data__analysis__v2__section_title'>The Mastery Framework</p>
                <div className='complete_data__analysis__v2__mastery_framework__content'>
                    <div className='complete_data__analysis__v2__mastery_framework__graph'>
                        <Graph parameters={report.rubric_parameters} />
                        <div className='complete_data__analysis__v2__mastery_framework__graph__legend'>
                            <div className='complete_data__analysis__v2__mastery_framework__graph__legend__item'>
                                <div className='complete_data__analysis__v2__mastery_framework__graph__legend__item__ico' />
                                <p>CURRENT PROFILE</p>
                            </div>
                            <div className='complete_data__analysis__v2__mastery_framework__graph__legend__item'>
                                <p className='complete_data__analysis__v2__mastery_framework__graph__legend__item__ico'>--</p>
                                <p>BENCHMARK ({report.milestone_level})</p>
                            </div>
                        </div>
                    </div>
                    <div className='complete_data__analysis__v2__mastery_framework__details'>
                        <div className='complete_data__analysis__v2__mastery_framework__details__item'>
                            <div className='complete_data__analysis__v2__mastery_framework__details__item__head'>
                                <div />
                                <p>COGNITIVE&nbsp;SKILLS</p>
                                <div />
                            </div>
                            <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters'>
                                {report.rubric_parameters.map(parameter => (
                                    <div
                                        key={parameter.title}
                                        className={`complete_data__analysis__v2__mastery_framework__details__item__parameters__item complete_data__analysis__v2__mastery_framework__details__item__parameters__item--${getRubricLevel(parameter.score).level}`}
                                    >
                                        <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__title'>
                                            <p>{parameter.title}</p>
                                            <Tooltip content={parameter.tooltip_content} />
                                        </div>
                                        <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress'>
                                            <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress__track'>
                                                <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress__track__left' />
                                                <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress__track__right' />
                                            </div>
                                            <div className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress__track_divider' />
                                            <div
                                                className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__progress__thumb'
                                                style={{ width: `${(parameter.score / 5) * 100}%` }}
                                            />
                                        </div>
                                        <p className='complete_data__analysis__v2__mastery_framework__details__item__parameters__item__score'>{parameter.score}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className='complete_data__analysis__v2__mastery_framework__details__descr'>
                            Shaded area represents the Benchmark threshold (4.0/5.0) for Director-level leadership.
                        </p>
                    </div>
                </div>
            </div>
            <div className="complete_data__analysis__v2__deep_dive">
                <p className='complete_data__analysis__v2__section_title'>Performance Deep-Dive: {report.simulation_name}</p>
                <table className='complete_data__analysis__v2__deep_dive__table d-none-mobile'>
                    <thead>
                        <tr>
                            <th><p>RUBRIC PARAMETER</p></th>
                            <th><p>LEVEL</p></th>
                            <th><p>RECOMMENDATIONS</p></th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.rubric_parameters.map(parameter => (
                            <tr
                                key={parameter.title}
                                style={{ '--rubric-color': getRubricLevel(parameter.score).color } as CSSProperties}
                            >
                                <td>
                                    <div className='complete_data__analysis__v2__deep_dive__table__param_title'>
                                        <p>{parameter.title}</p>
                                        <Tooltip content={parameter.tooltip_content} />
                                    </div>
                                    <div className="complete_data__analysis__v2__deep_dive__table__param_border" />
                                </td>
                                <td><p className='complete_data__analysis__v2__deep_dive__table__param_level'>{getRubricLevel(parameter.score).title}</p></td>
                                <td><p className='complete_data__analysis__v2__deep_dive__table__param_descr'>{parameter.recommendation}</p></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className='complete_data__analysis__v2__deep_dive__cards d-none-desktop'>
                    {report.rubric_parameters.map(parameter => (
                        <div
                            key={parameter.title}
                            className='complete_data__analysis__v2__deep_dive__cards__card'
                            style={{ '--rubric-color': getRubricLevel(parameter.score).color } as CSSProperties}
                        >
                            <div className='complete_data__analysis__v2__deep_dive__cards__card__head'>
                                <div className='complete_data__analysis__v2__deep_dive__cards__card__param_title'>
                                    <p>{parameter.title}</p>
                                    <Tooltip content={parameter.tooltip_content} />
                                </div>
                                <p className='complete_data__analysis__v2__deep_dive__cards__card__param_level'>{getRubricLevel(parameter.score).title}</p>
                            </div>
                            <p className='complete_data__analysis__v2__deep_dive__cards__card__param_descr_subtitle'>Recommendations</p>
                            <p className='complete_data__analysis__v2__deep_dive__cards__card__param_descr'>{parameter.recommendation}</p>
                        </div>
                    ))}
                </div>
            </div>
            <BehavioralAudit
                situation={report.situation}
                current_role={report.current_role}
                rapport_collapses={report.rapport_collapses}
                rapport_collapse_conversations={report.rapport_collapse_conversations}
            />
        </div>
    );
};


interface GraphProps {
    parameters: ISRV2RubricParameter[];
};
const Graph = (props: GraphProps) => {
    const { parameters } = props;

    const MAX_SCORE = 5;
    const AXIS_ORDER = parameters.map(p => p.short_title);
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleRadians: number) => {
        return {
            x: centerX + radius * Math.cos(angleRadians),
            y: centerY + radius * Math.sin(angleRadians),
        };
    };

    const buildPolygonPoints = (oParameters: typeof parameters, maxRadius: number) => {
        const centerX = 50;
        const centerY = 50;
        const angleStep = (2 * Math.PI) / AXIS_ORDER.length;

        return AXIS_ORDER.map((axis, index) => {
            const param = oParameters.find(p => p.short_title === axis);
            const score = param ? param.score : 0;

            const radius = (score / MAX_SCORE) * maxRadius;
            const angle = angleStep * index - Math.PI / 2; // rotate so first axis is top

            const { x, y } = polarToCartesian(centerX, centerY, radius, angle);
            return `${x},${y}`;
        }).join(' ');
    };

    const currentProfilePoints = buildPolygonPoints(parameters, 32);
    const forceMultiplierPoints = buildPolygonPoints(parameters.map(p => ({ ...p, score: 4.5 })), 32);

    return (
        <svg viewBox="-25 -20 150 140" className="complete_data__analysis__v2__mastery_framework__graph__diagram">
            {/* Grid */}
            {[10, 20, 30, 40].map(r => <circle key={r} r={r} />)}

            {/* Force Multiplier (L5) */}
            <polygon points={forceMultiplierPoints} className="target" />

            {/* Current Profile */}
            <polygon points={currentProfilePoints} className="current" />

            {/* Axis labels */}
            {AXIS_ORDER.map((label, index) => {
                const angle = (2 * Math.PI / AXIS_ORDER.length) * index - Math.PI / 2;
                const { x, y } = polarToCartesian(50, 50, 45, angle);

                return <text key={label} x={x} y={y}>{label}</text>;
            })}
        </svg>
    );
};

interface TooltipProps {
    content: string;
};
const Tooltip = ({ content }: TooltipProps) => {
    return (
        <div className='complete_data__analysis__v2__tooltip'>
            <Info className='complete_data__analysis__v2__tooltip__btn' />
            <div className='complete_data__analysis__v2__tooltip__content' dangerouslySetInnerHTML={{ __html: content }} />
        </div>
    );
};

const ExecutiveNarrativeContentDetails = ({ score, title, custClass }: { score: number; title: string; custClass: string; }) => {
    return (
        <div className={`complete_data__analysis__v2__executive_narrative__content__details ${custClass}`}>
            <div className='complete_data__analysis__v2__executive_narrative__content__details__scores'>
                <p>{score}<span>/5.0</span></p>
                <p>{title}</p>
            </div>
            <div className='complete_data__analysis__v2__executive_narrative__content__details__profidx'>
                <p>PROFICIENCY INDEX</p>
                <Tooltip
                    content={`
                        <span>
                            Leadership Competency Proficiency Levels:
                            <br/>• Highly Skilled (4.0-5.0): Sophisticated leadership with nuanced application
                            <br/>• Proficient (3.0-3.9): Solid foundations with room for elevation
                            <br/>• Emerging (2.0-2.9): Emerging skills needing deliberate development
                            <br/>• Practicing (0-1.9): Early stages requiring focused practice
                        <span>
                    `}
                />
            </div>
        </div>
    );
};

interface BehavioralAudiProps {
    situation: string;
    current_role: string;
    rapport_collapses: ISRV2RapportCollapse[];
    rapport_collapse_conversations: ISRV2RapportCollapseConversation[];
};
const BehavioralAudit = (props: BehavioralAudiProps) => {
    const { rapport_collapses, rapport_collapse_conversations } = props;

    const [selectedCollapse, setSelectedCollapse] = useState<{ idx: number; collapse: ISRV2RapportCollapse } | null>(null);
    const [selectedConversation, setSelectedConversation] = useState<ISRV2RapportCollapseConversation | null>(null);

    useEffect(() => {
        if (!rapport_collapses.length) return;

        setSelectedCollapse({ idx: 0, collapse: rapport_collapses[0] });
    }, [rapport_collapses]);

    useEffect(() => {
        if (!rapport_collapse_conversations.length || !selectedCollapse) return;

        const convo = rapport_collapse_conversations.find(c => c.id === selectedCollapse.collapse.conversation_id);
        if (!convo) return;

        setSelectedConversation(convo);
    }, [rapport_collapse_conversations, selectedCollapse]);

    const getCollapseLeft = (sessionStart: string, sessionEnd: string, timestamp: string): number => {
        const start = new Date(sessionStart).getTime();
        const end = new Date(sessionEnd).getTime();
        const mid = new Date(timestamp).getTime();

        const totalSeconds = (end - start) / 1000;
        const elapsedSeconds = (mid - start) / 1000;
        return (elapsedSeconds / totalSeconds) * 100;
    };

    const prevCollapse = () => {
        if (!selectedCollapse) return;
        if (selectedCollapse.idx <= 0) return;

        const newIdx = selectedCollapse.idx - 1;
        const clps = rapport_collapses[newIdx];

        setSelectedCollapse({ idx: newIdx, collapse: clps });

        if (selectedConversation) {
            const convo = rapport_collapse_conversations.find(c => c.id === clps.conversation_id);
            if (!convo) return;

            setSelectedConversation(convo);
        };
    };

    const nextCollapse = () => {
        if (!selectedCollapse) return;
        if (selectedCollapse.idx >= (rapport_collapses.length - 1)) return;

        const newIdx = selectedCollapse.idx + 1;
        const clps = rapport_collapses[newIdx];

        setSelectedCollapse({ idx: newIdx, collapse: clps });

        if (selectedConversation) {
            const convo = rapport_collapse_conversations.find(c => c.id === clps.conversation_id);
            if (!convo) return;

            setSelectedConversation(convo);
        };
    };

    const getElapsedLabel = (sessionStart: string, timestamp: string) => {
        const start = new Date(sessionStart).getTime();
        const ts = new Date(timestamp).getTime();

        const elapsedSeconds = Math.floor((ts - start) / 1000);
        const minutes = Math.floor(elapsedSeconds / 60);
        const seconds = elapsedSeconds % 60;

        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const getDistinctStageKeys = () => {
        return rapport_collapse_conversations.map(c => c.stage_key);
    };

    const handleChangeStage = (stage_key: ESimulationStageKey) => {
        const rpConvo = rapport_collapse_conversations.find(rpc => rpc.stage_key === stage_key);
        if (!rpConvo) return;

        const rapportCollapseIdx = rapport_collapses.findIndex(rc => rpConvo.stage_key === stage_key);
        if (rapportCollapseIdx === -1) return;

        setSelectedCollapse({ idx: rapportCollapseIdx, collapse: rapport_collapses[rapportCollapseIdx] });

        if (selectedConversation) {
            setSelectedConversation(rpConvo);
        };
    };

    useEffect(() => {
        if (!selectedCollapse || !selectedConversation) return;

        requestAnimationFrame(() => {
            const el = document.getElementById(`chat_msg--${selectedCollapse.collapse.chat_id}`);
            if (!el) return;

            el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
    }, [selectedConversation, selectedCollapse]);

    return (
        <div className="complete_data__analysis__v2__behavioral_audit">
            <p className='complete_data__analysis__v2__section_title'>SIMULATION LAB: BEHAVIORAL AUDIT</p>
            {getDistinctStageKeys().length > 1 && !!selectedConversation && (
                <div className='complete_data__analysis__v2__behavioral_audit__stages'>
                    {getDistinctStageKeys().map(sk => (
                        <button
                            key={sk}
                            className={`complete_data__analysis__v2__behavioral_audit__stages__stage_btn ${sk === selectedConversation.stage_key ? 'complete_data__analysis__v2__behavioral_audit__stages__stage_btn--active' : ''}`}
                            onClick={() => handleChangeStage(sk)}
                        >
                            {sk[0].toUpperCase() + sk.slice(1).replace('-', ' ')}
                        </button>
                    ))}
                </div>
            )}
            {/* <div
                className='complete_data__analysis__v2__behavioral_audit__situation'
                dangerouslySetInnerHTML={{ __html: `<p><strong>Situation: </strong></p>${situation}` }}
            /> */}
            {!!selectedCollapse && !!selectedConversation && (
                <div className='complete_data__analysis__v2__behavioral_audit__content'>
                    <div className='complete_data__analysis__v2__behavioral_audit__content__left'>
                        <p className='complete_data__analysis__v2__behavioral_audit__content__title'>Interaction Heatmap</p>
                        <p className='complete_data__analysis__v2__behavioral_audit__content__descr'>Tracking the correlation between your dialogue choices and stakeholder rapport.</p>
                        <div className='complete_data__analysis__v2__behavioral_audit__content__rapport'>
                            <div className='complete_data__analysis__v2__behavioral_audit__content__rapport__legend'>
                                <p>SESSION START</p>
                                <p>
                                    FRICTION POINTS
                                    {/* {selectedConversation.status === 'successful' ? 'FRICTION' : selectedConversation.status === 'stalemate' ? 'COLLAPSE/FRICTION' : 'RAPPORT COLLAPSE'} */}
                                </p>
                                <p>SESSION END</p>
                            </div>
                            <div className={`complete_data__analysis__v2__behavioral_audit__content__rapport__bar complete_data__analysis__v2__behavioral_audit__content__rapport__bar--${selectedConversation.status}`}>
                                {!!rapport_collapses.length &&
                                    <div
                                        className='complete_data__analysis__v2__behavioral_audit__content__rapport__bar__divider'
                                        style={{ left: `${getCollapseLeft(selectedConversation.start, selectedConversation.end, selectedCollapse.collapse.timestamp)}%` }}
                                    />
                                }
                            </div>
                            <div className='complete_data__analysis__v2__behavioral_audit__content__rapport__analysis'>
                                <p>At <strong>{getElapsedLabel(selectedConversation.start, selectedCollapse.collapse.timestamp)}</strong>, {selectedCollapse.collapse.analysis}</p>
                            </div>
                        </div>
                        <div className='complete_data__analysis__v2__behavioral_audit__content__session_highlight'>
                            <MessagesSquare />
                            <p>Session Highlight: {selectedConversation.highlight}</p>
                            <p>Analysis: <span>"{selectedConversation.analysis}"</span></p>
                        </div>
                    </div>
                    {!!rapport_collapses.length && (
                        <div className='complete_data__analysis__v2__behavioral_audit__content__right'>
                            <p className='complete_data__analysis__v2__behavioral_audit__content__title'>Response Analysis</p>
                            <p className='complete_data__analysis__v2__behavioral_audit__content__descr'>Learn what you could've done differently.</p>
                            <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse'>
                                <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__head'>
                                    <p>Observed Responses</p>
                                    {rapport_collapses.length > 1 && (
                                        <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__head__nav'>
                                            <p>{selectedCollapse.idx + 1}/{rapport_collapses.length}</p>
                                            <button
                                                disabled={selectedCollapse.idx === 0}
                                                onClick={prevCollapse}
                                            >
                                                <ChevronLeft />
                                            </button>
                                            <button
                                                disabled={selectedCollapse.idx >= (rapport_collapses.length - 1)}
                                                onClick={nextCollapse}
                                            >
                                                <ChevronRight />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__list'>
                                    <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__list__item--opening'>
                                        <p>{getElapsedLabel(selectedConversation.start, selectedCollapse.collapse.timestamp)} - {selectedCollapse.collapse.context}</p>
                                    </div>
                                    <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__list__item--response'>
                                        <p>Your Response</p>
                                        <p>"{selectedCollapse.collapse.message}"</p>
                                    </div>
                                    <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__list__item--impact'>
                                        <p>Impact Analysis</p>
                                        <p>{selectedCollapse.collapse.impact_analysis}</p>
                                    </div>
                                    <div className='complete_data__analysis__v2__behavioral_audit__content__rapport_collapse__list__item--recommended'>
                                        <p>Recommended Alternative</p>
                                        <p>"{selectedCollapse.collapse.recommended_alternative}"</p>
                                        <p>Strategic Shift</p>
                                        <p>{selectedCollapse.collapse.strategic_shift}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};